/* Delete a workspace skill — refused while any agent has it, attached or waiting for
   its owner, with those agents listed to go to. Published versions that froze the skill
   keep running: its files are stored by content and never removed. */
import React from "react";
import { ChevronRight } from "lucide-react";

import { Chip } from "@/shared/components/ui-kit";
import { confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { SKILL_CODES } from "@/shared/lib/skill-rules";

import { useDeleteSkill } from "../hooks/useSkillWrites";

import type { ApiError } from "@/shared/api/errors";
import type { SkillDetail, SkillUse } from "@/shared/types";

export default function DeleteSkillDialog({ skill, onDeleted, onOpenAgent, onClose }: {
  skill: SkillDetail;
  onDeleted: () => void;
  onOpenAgent?: (agentId: string) => void;
  onClose: () => void;
}) {
  const del = useDeleteSkill();
  const error = del.error as ApiError | null;
  // the server's list wins: an agent may have taken it since the page loaded
  const users: Pick<SkillUse, "agent_id" | "name">[] = error?.code === SKILL_CODES.inUse
    ? (error.details ?? []).map((d) => ({ agent_id: String(d.agent_id), name: String(d.name) }))
    : skill.used_by;
  const blocked = users.length > 0;
  const n = users.length;

  return (
    <AlertDialog open onOpenChange={(open) => !open && !del.isPending && onClose()}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[440px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">
            {blocked ? `${skill.name} is used by ${n === 1 ? "an agent" : `${n} agents`}` : `Delete ${skill.name}?`}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">
            {blocked
              ? `Remove it from ${n === 1 ? "that agent" : "them"} first, in ${n === 1 ? "its" : "each one's"} Settings. A request still waiting for an owner counts too.`
              : "It leaves the workspace's skills, and no agent can add it again. Published agent versions that froze it keep running."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {blocked && (
          <div className="rounded-[12px] ring-1 ring-[var(--hairline)] bg-[var(--surface)] divide-y divide-[var(--hairline)] overflow-hidden">
            {users.map((u) => {
              const status = skill.used_by.find((s) => s.agent_id === u.agent_id)?.status;
              return (
                <button
                  key={u.agent_id}
                  type="button"
                  disabled={!onOpenAgent}
                  onClick={() => { onClose(); onOpenAgent?.(u.agent_id); }}
                  className="press w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-[var(--accent-hover)] disabled:pointer-events-none"
                >
                  <span className="min-w-0 flex-1 truncate text-[13px] text-[var(--text-primary)]">{u.name}</span>
                  {status === "requested" && <Chip>Waiting for its owner</Chip>}
                  {onOpenAgent && <ChevronRight className="w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)]" strokeWidth={2} />}
                </button>
              );
            })}
          </div>
        )}
        {error && error.code !== SKILL_CODES.inUse && <p role="alert" className="m-0 text-[12.5px] text-[var(--danger)]">{error.message}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">{blocked ? "Close" : "Cancel"}</AlertDialogCancel>
          {!blocked && (
            <button className={confirmDangerCls} disabled={del.isPending} onClick={() => del.mutate(skill.id, { onSuccess: onDeleted })}>
              {del.isPending ? "Deleting…" : "Delete"}
            </button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
